import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Mail,
  Lock,
  ArrowRight,
  Loader2,
  CheckCircle,
  Mail as MailIcon,
} from "lucide-react";
import { supabase } from "../lib/supabase";
import { isOlderBrowser } from "../utils/browserDetection";
import { AuthCompat } from "./AuthCompat";

type AuthMode = "signin" | "signup" | "reset" | "confirmation";

// Destino post-login: si hay un ?next= relativo válido, va ahí; sino, a "/".
// Solo paths relativos same-origin (empiezan con "/" y no "//") para evitar
// open-redirect a sitios externos.
function getPostLoginDest(): string {
  try {
    const next = new URLSearchParams(window.location.search).get("next");
    if (next && next.startsWith("/") && !next.startsWith("//")) return next;
  } catch {}
  return "/";
}

export const Auth = () => {
  // Check if this is an older browser
  const [isCompat, setIsCompat] = useState(false);

  useEffect(() => {
    try {
      // Detect if this is an older browser
      const needsCompat = isOlderBrowser();
      setIsCompat(needsCompat);

      if (needsCompat) {
        console.log("Using compatibility mode for older browser");
      }
    } catch (err) {
      console.error("Error during browser detection:", err);
      // If detection fails, default to compatibility mode
      setIsCompat(true);
    }
  }, []);

  // Render the compatibility version for older browsers
  if (isCompat) {
    return <AuthCompat />;
  }

  const [mode, setMode] = useState<AuthMode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);
  const [resetCooldown, setResetCooldown] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  // Capturamos el destino post-login UNA sola vez al montar (cuando la URL
  // todavía es /auth?next=...). Si lo recalculáramos en cada navigate, el
  // segundo navigate leería la URL ya cambiada (sin ?next=) y caería a "/".
  const [postLoginDest] = useState(getPostLoginDest);

  // If user is already logged in (e.g. OAuth redirect landed here), go to home
  useEffect(() => {
    // Check if there's already a session (e.g. OAuth just completed)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        navigate(postLoginDest, { replace: true });
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === "SIGNED_IN" && session) {
        // For OAuth signups, save UTM/Meta cookies and ref code, and fire webhook
        const userId = session.user.id;
        const isOAuth = session.user.app_metadata?.provider !== "email";
        // Distinguish SIGNUP (new user) from SIGNIN (returning user):
        // if created_at is within the last 60s, treat as a fresh signup.
        const createdAt = session.user.created_at
          ? new Date(session.user.created_at).getTime()
          : 0;
        const isFreshSignup = createdAt > 0 && Date.now() - createdAt < 60_000;
        if (isOAuth && isFreshSignup) {
          try {
            const utmData: Record<string, string | null> = {};
            for (const key of [
              "utm_source",
              "utm_medium",
              "utm_campaign",
              "utm_term",
              "utm_content",
            ] as const) {
              utmData[key] = localStorage.getItem(`atlas:${key}`) || null;
            }
            utmData.fbc = localStorage.getItem("atlas:fbc") || null;
            utmData.fbp = localStorage.getItem("atlas:fbp") || null;

            // Ref code
            let refCode: string | null = null;
            try {
              const params = new URLSearchParams(window.location.search);
              refCode = params.get("ref");
              if (!refCode) refCode = localStorage.getItem("atlas:ref");
            } catch {}
            if (refCode) utmData.ref_code = refCode;

            const hasAny = Object.values(utmData).some((v) => v !== null);
            if (hasAny) {
              await supabase.from("profiles").update(utmData).eq("id", userId);
            }
          } catch {}

          // Registration webhook
          try {
            await fetch(
              "https://ai-agency-dt7d.onrender.com/webhook/resgistro_user",
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: session.user.email }),
              },
            );
          } catch {}
        }
        navigate(postLoginDest, { replace: true });
      }
    });
    return () => subscription.unsubscribe();
  }, [navigate]);

  // Initialize/auth mode from URL (e.g., /auth?mode=signup)
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const m = params.get("mode");
      if (m === "signup" || m === "signin" || m === "reset") {
        setMode(m as AuthMode);
      }
    } catch (e) {
      // ignore parsing errors
    }
  }, [location.search]);

  // Capture UTM params, ref code, and Meta cookies on page load
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const utmKeys = [
        "utm_source",
        "utm_medium",
        "utm_campaign",
        "utm_term",
        "utm_content",
      ] as const;
      for (const key of utmKeys) {
        const val = params.get(key);
        if (val) {
          try {
            localStorage.setItem(`atlas:${key}`, val);
          } catch {}
        }
      }
      // Capture ref code from URL
      const ref = params.get("ref");
      if (ref) {
        try {
          localStorage.setItem("atlas:ref", ref);
        } catch {}
      }
      // Capture Meta _fbc and _fbp cookies
      const getCookie = (name: string): string | null => {
        const match = document.cookie.match(
          new RegExp("(?:^|; )" + name + "=([^;]*)"),
        );
        return match ? decodeURIComponent(match[1]) : null;
      };
      const fbc = getCookie("_fbc");
      const fbp = getCookie("_fbp");
      if (fbc) {
        try {
          localStorage.setItem("atlas:fbc", fbc);
        } catch {}
      }
      if (fbp) {
        try {
          localStorage.setItem("atlas:fbp", fbp);
        } catch {}
      }
    } catch {}
  }, []);

  const handleResendConfirmation = async () => {
    if (!email || resendCooldown > 0 || resendLoading) return;
    setError(null);
    setMessage(null);
    setResendLoading(true);
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email,
      });
      if (error) throw error;
      setMessage("Te enviamos un nuevo correo de confirmación.");
      setResendCooldown(60);
    } catch (err: any) {
      setError(err?.message || "No se pudo reenviar el correo de confirmación");
    } finally {
      setResendLoading(false);
    }
  };

  useEffect(() => {
    if (!resendCooldown) return;
    const id = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(id);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [resendCooldown]);

  useEffect(() => {
    if (!resetCooldown) return;
    const id = setInterval(() => {
      setResetCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(id);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [resetCooldown]);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    // Check if terms are accepted when signing up
    if (mode === "signup" && !acceptedTerms) {
      setError("Debes aceptar los términos y condiciones para continuar");
      return;
    }

    setLoading(true);

    try {
      if (mode === "signin") {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) {
          // Check if the error is about email not being confirmed
          if (error.message?.includes("Email not confirmed")) {
            throw new Error(
              "Email no confirmado. Por favor, revisa tu bandeja de entrada y haz clic en el enlace de confirmación para activar tu cuenta.",
            );
          }
          throw error;
        }

        // After successful sign-in, delegate onboarding decisions to ProtectedRoute
        navigate(postLoginDest, { replace: true });
      } else if (mode === "signup") {
        // Capture referral code from URL or localStorage and send as metadata
        let refCode: string | null = null;
        try {
          const params = new URLSearchParams(window.location.search);
          refCode = params.get("ref");
          if (!refCode) {
            try {
              refCode = localStorage.getItem("atlas:ref");
            } catch {}
          } else {
            try {
              localStorage.setItem("atlas:ref", refCode);
            } catch {}
          }
        } catch {}

        const signUpPayload: {
          email: string;
          password: string;
          options?: { data: { ref_code: string } };
        } = {
          email,
          password,
        };
        if (refCode) {
          signUpPayload.options = { data: { ref_code: refCode } };
        }

        const { data, error } = await supabase.auth.signUp(signUpPayload);
        if (error) throw error;

        // Save UTM params and Meta cookies to profile
        if (data?.user?.id) {
          try {
            const utmData: Record<string, string | null> = {};
            for (const key of [
              "utm_source",
              "utm_medium",
              "utm_campaign",
              "utm_term",
              "utm_content",
            ] as const) {
              utmData[key] = localStorage.getItem(`atlas:${key}`) || null;
            }
            utmData.fbc = localStorage.getItem("atlas:fbc") || null;
            utmData.fbp = localStorage.getItem("atlas:fbp") || null;
            const hasAny = Object.values(utmData).some((v) => v !== null);
            if (hasAny) {
              await supabase
                .from("profiles")
                .update(utmData)
                .eq("id", data.user.id);
            }
          } catch {}
        }

        // Send registration webhook
        try {
          await fetch(
            "https://ai-agency-dt7d.onrender.com/webhook/resgistro_user",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ email }),
            },
          );
        } catch (webhookError) {
          console.error("Error sending registration webhook:", webhookError);
        }

        // If email confirmation is disabled, user is automatically signed in
        // Navigate to home/onboarding
        if (data?.session) {
          navigate(postLoginDest, { replace: true });
        } else {
          // Fallback to confirmation screen if session not created (email confirmation enabled)
          setMode("confirmation");
        }
      } else if (mode === "reset") {
        // Block if already in cooldown to prevent multiple emails
        if (resetCooldown > 0) {
          setError(
            `Por favor esperá ${resetCooldown} segundos antes de solicitar otro email.`,
          );
          setLoading(false);
          return;
        }

        // Send reset email and wait for response
        const { error: resetError } = await supabase.auth.resetPasswordForEmail(
          email,
          {
            redirectTo: `${window.location.origin}/restablecer-clave`,
          },
        );

        if (resetError) {
          console.error("Error sending reset email:", resetError);
        }

        // Set cooldown to prevent multiple requests (60 seconds)
        setResetCooldown(60);
        setMessage(
          "Si existe una cuenta con este email, recibirás instrucciones para restablecer tu contraseña.",
        );
      }
    } catch (error) {
      if (error instanceof Error) {
        // Check for invalid login credentials error and show Spanish message
        if (error.message === "Invalid login credentials") {
          setError("Correo electrónico o contraseña incorrectos");
        } else {
          setError(error.message);
        }
      } else {
        setError("Ocurrió un error inesperado");
      }
    } finally {
      setLoading(false);
    }
  };

  const [googleLoading, setGoogleLoading] = useState(false);

  const handleGoogleAuth = async () => {
    setError(null);
    setGoogleLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/auth`,
        },
      });
      if (error) throw error;
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Error al iniciar sesión con Google",
      );
      setGoogleLoading(false);
    }
  };

  // Reusable Auth Card content (used in mobile and desktop)
  const renderAuthCard = () => (
    <div
      className="bg-surface-card md:rounded-lg md:shadow-md px-6 md:px-10 pt-8 h-full md:flex md:flex-col md:justify-center overflow-auto"
      style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 2rem)' }}
    >
      <h2 className="text-[17px] md:text-[17px] font-semibold text-brand-black/90 mb-10 md:mb-2 text-center">
        {mode === "signin" && "Iniciar Sesión"}
        {mode === "signup" && "Crea tu cuenta y obtené 10 fotos gratis"}
        {mode === "reset" && "Restablecer Contraseña"}
        {mode === "confirmation" && "Confirma tu Email"}
      </h2>
      <p className="hidden md:block text-[15px] text-neutral-500 mb-10 text-center">
        {mode === "signin" && "Bienvenido de vuelta a Atlas"}
        {mode === "signup" && "Únete a Atlas para empezar"}
        {mode === "reset" && "Ingresa tu email para restablecer tu contraseña"}
        {mode === "confirmation" &&
          "Hemos enviado un correo de confirmación a tu email"}
      </p>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-sm text-red-700 text-[13px]">
          {error}
        </div>
      )}

      {message && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-sm text-green-700 text-[13px]">
          {message}
        </div>
      )}

      {/* Google OAuth button — shown on signin and signup */}
      {(mode === "signin" || mode === "signup") && (
        <>
          <button
            type="button"
            onClick={handleGoogleAuth}
            disabled={googleLoading}
            className="w-full h-11 px-4 rounded-md border border-neutral-200 bg-surface-card text-[15px] font-medium text-brand-black/90 flex items-center justify-center gap-3 hover:bg-neutral-50 active:bg-neutral-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
          >
            {googleLoading ? (
              <Loader2 className="w-5 h-5 animate-spin text-neutral-400" />
            ) : (
              <>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z"
                    fill="#4285F4"
                  />
                  <path
                    d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z"
                    fill="#34A853"
                  />
                  <path
                    d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58Z"
                    fill="#EA4335"
                  />
                </svg>
                {mode === "signin"
                  ? "Iniciar sesión con Google"
                  : "Registrarse con Google"}
              </>
            )}
          </button>

          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-neutral-200" />
            </div>
            <div className="relative flex justify-center text-[13px]">
              <span className="bg-surface-card px-3 text-neutral-400">
                o con email
              </span>
            </div>
          </div>
        </>
      )}

      {mode === "confirmation" ? (
        <div className="space-y-6">
          <div className="flex flex-col items-center justify-center py-6">
            <div className="bg-brand-primary/10 rounded-full p-4 mb-4">
              <MailIcon className="w-10 h-10 text-brand-primary" />
            </div>
            <p className="text-center text-brand-black/70 text-[15px] mb-2">
              Hemos enviado un correo de confirmación a{" "}
              <span className="font-semibold text-brand-black/90">{email}</span>
            </p>
            <p className="text-center text-neutral-500 text-[13px]">
              Por favor, revisa tu bandeja de entrada y haz clic en el enlace de
              confirmación para activar tu cuenta.
            </p>
          </div>

          <button
            onClick={() => setMode("signin")}
            className="w-full bg-brand-primary hover:bg-brand-primary-hover text-white h-11 px-4 rounded-md shadow-sm text-[15px] font-medium flex items-center justify-center gap-2 transition-colors"
          >
            Ya lo confirmé
            <CheckCircle className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={handleResendConfirmation}
            disabled={resendCooldown > 0 || resendLoading}
            className="w-full mt-2 text-brand-primary hover:text-brand-primary-hover text-[13px] bg-transparent border-none disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {resendCooldown > 0 ? (
              <>
                Reenviar mail
                <br />
                {`(Podés reintentar en ${resendCooldown} seg)`}
              </>
            ) : (
              "Reenviar mail"
            )}
          </button>
        </div>
      ) : (
        <form onSubmit={handleAuth} className="space-y-4">
          <div>
            <label
              htmlFor="email"
              className="block text-[13px] font-medium text-brand-black/70 mb-1.5"
            >
              Email
            </label>
            <div className="relative">
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 h-11 border border-neutral-200 rounded-md text-[15px] text-brand-black/90 placeholder:text-neutral-400 focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-colors"
                placeholder="tu@email.com"
                required
              />
              <Mail className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
            </div>
          </div>

          {mode !== "reset" && (
            <div>
              <label
                htmlFor="password"
                className="block text-[13px] font-medium text-brand-black/70 mb-1.5"
              >
                Contraseña
              </label>
              <div className="relative">
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 h-11 border border-neutral-200 rounded-md text-[15px] text-brand-black/90 placeholder:text-neutral-400 focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-colors"
                  placeholder="••••••••"
                  required
                />
                <Lock className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
              </div>
            </div>
          )}

          {mode === "signup" && (
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="terms"
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="w-4 h-4 border border-neutral-300 rounded accent-brand-primary focus:ring-2 focus:ring-brand-primary/30"
                  required
                />
              </div>
              <div className="ml-3 text-[13px]">
                <label htmlFor="terms" className="text-neutral-500">
                  Acepto los{" "}
                  <a
                    href="https://estudio-atlas.com/politica-de-privacidad"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-primary hover:text-brand-primary-hover transition-colors"
                  >
                    términos y condiciones
                  </a>
                </label>
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-brand-primary hover:bg-brand-primary-hover text-white h-11 px-4 rounded-md shadow-sm text-[15px] font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <>
                {mode === "reset" ? "Enviando mail" : null}
                <Loader2 className="w-5 h-5 animate-spin" />
              </>
            ) : (
              <>
                {mode === "signin" && "Iniciar Sesión"}
                {mode === "signup" && "Crear Cuenta"}
                {mode === "reset" && "Enviar Instrucciones"}
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>
        </form>
      )}

      <div className="mt-6 text-center text-[13px]">
        {mode === "signin" && (
          <>
            <button
              onClick={() => setMode("reset")}
              className="text-brand-primary hover:text-brand-primary-hover transition-colors"
            >
              ¿Olvidaste tu contraseña?
            </button>
            <div className="mt-2 text-neutral-500">
              ¿No tienes una cuenta?{" "}
              <button
                onClick={() => setMode("signup")}
                className="text-brand-primary hover:text-brand-primary-hover transition-colors"
              >
                Regístrate
              </button>
            </div>
          </>
        )}
        {mode === "signup" && (
          <div className="text-neutral-500">
            ¿Ya tienes una cuenta?{" "}
            <button
              onClick={() => setMode("signin")}
              className="text-brand-primary hover:text-brand-primary-hover transition-colors"
            >
              Inicia Sesión
            </button>
          </div>
        )}
        {mode === "reset" && (
          <button
            onClick={() => setMode("signin")}
            className="text-brand-primary hover:text-brand-primary-hover transition-colors"
          >
            Volver a Iniciar Sesión
          </button>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile layout */}
      <div className="md:hidden h-[100dvh] bg-surface-card flex flex-col px-0 pb-0">
        {/* Top: Logo area with fixed spacing. En PWA standalone con
            `black-translucent`, la status bar se overlapea con el
            contenido — sumamos `env(safe-area-inset-top)` al padding-top
            para que el logo no quede pegado al reloj. */}
        <div
          className="w-full pb-8 flex items-center justify-center"
          style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 3rem)' }}
        >
          <div className="flex items-center gap-3">
            <img
              src="/images/estudio-atlas-logo-solo.svg"
              alt="Atlas logo"
              className="w-16 h-16"
            />
          </div>
        </div>
        {/* Bottom: Auth Card consumes remaining space */}
        <div className="w-full flex-1">{renderAuthCard()}</div>
      </div>

      {/* Desktop layout */}
      <div className="hidden md:block relative h-screen overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/foto-login.png')",
          }}
        />

        {/* Foreground content */}
        <div className="relative z-10 flex h-screen">
          {/* Left side: brand top-left, message bottom-left */}
          <div className="flex-1 flex flex-col justify-between p-8 h-full">
            <div className="flex items-center gap-3">
              <img
                src="/images/estudio-atlas-logo-solo-blanco.svg"
                alt="Atlas logo"
                className="w-9 h-9"
              />
              <img
                src="/images/estudio-atlas-nombre-solo-blanco.svg"
                alt="Estudio Atlas"
                className="h-5"
              />
            </div>

            <div className="text-white">
              <div className="text-2xl md:text-3xl font-semibold">
                Tu estudio de fotos, sin estudio
              </div>
              <div className="text-[15px] mt-2 text-white/80">
                Modelos virtuales y fondos listos para e-commerce e Instagram.
              </div>
            </div>
          </div>

          {/* Right side: auth card */}
          <div className="w-1/2 p-8 flex items-stretch h-screen">
            <div className="w-full ml-auto h-full">{renderAuthCard()}</div>
          </div>
        </div>
      </div>
    </>
  );
};
